import React, { useState, useEffect } from "react";
import "./ExploreRange.css";
import { useNavigate } from "react-router-dom";

// ── Specs per car ──────────────────────────────────────────────
const CAR_SPECS = {
  SELTOS:          { price: "₹10.99 Lakh*", mileage: "17.8 kmpl", fuel: "Petrol / Diesel" },
  SONET:           { price: "₹7.31 Lakh*",  mileage: "18.3 kmpl", fuel: "Petrol / Diesel / CNG" },
  SYROS:           { price: "₹8.39 Lakh*",  mileage: "19.3 kmpl", fuel: "Petrol / Diesel" },
  "CARENS CLAVIS": { price: "₹11.20 Lakh*", mileage: "18.0 kmpl", fuel: "Petrol / Diesel" },
  CARENS:          { price: "₹10.99 Lakh*", mileage: "16.5 kmpl", fuel: "Petrol / Diesel / CNG" },
  CARNIVAL:        { price: "On Request",   mileage: "13.9 kmpl", fuel: "Diesel" },
  "CARENS CLAVIS EV": { price: "₹17.99 Lakh*", mileage: "390 km range", fuel: "Electric" },
  EV6:             { price: "On Request",   mileage: "520 km range", fuel: "Electric" },
  EV9:             { price: "On Request",   mileage: "561 km range", fuel: "Electric" },
  "SYROS EV":      { price: "₹13.50 Lakh*", mileage: "526 km range", fuel: "Electric" },
  SORENTO:         { price: "₹27.99 Lakh*", mileage: "Hybrid / AWD", fuel: "Hybrid / Diesel" },
};

// ── SVG Icons ──────────────────────────────────────────────────
const MileageIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const FuelIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 22V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v14"/>
    <path d="M3 22h12"/>
    <path d="M15 4h1a2 2 0 0 1 2 2v3a2 2 0 0 0 2 2h0v7a2 2 0 0 1-2 2"/>
    <line x1="7" y1="14" x2="11" y2="14"/>
  </svg>
);

const EVIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

// ── Main Component ─────────────────────────────────────────────
export default function ExploreRange({
  title = "Explore The Range",
  categories = [],
  data = [],
}) {
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const navigate = useNavigate();

  const filteredCars = data.filter((item) => item.category === activeCategory);

  useEffect(() => {
    const cards = document.querySelectorAll(".er-card");
    cards.forEach((card, i) => {
      card.style.opacity = "0";
      card.style.transform = "translateY(28px)";
      setTimeout(() => {
        card.style.transition = "opacity 0.48s ease, transform 0.48s ease";
        card.style.opacity = "1";
        card.style.transform = "translateY(0)";
      }, i * 100);
    });
  }, [activeCategory]);

  return (
    <section className="er-section">
      {/* Header row */}
      <div className="er-header">
        <h2 className="er-title">{title}</h2>
        <div className="er-tabs">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`er-tab${activeCategory === cat ? " er-tab--active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div className="er-grid">
        {filteredCars.map((item) => (
          <CarCard key={item.id} item={item} navigate={navigate} />
        ))}
      </div>
    </section>
  );
}

// ── Card ───────────────────────────────────────────────────────
function CarCard({ item, navigate }) {
  const [loaded, setLoaded] = useState(false);
  const specs = CAR_SPECS[item.name] || {};
  const isEV = item.category === "EV";

  return (
    <div className="er-card">
      {/* Image */}
      <div className="er-img-wrap">
        {!loaded && <div className="er-skeleton" />}
        <img
          src={item.image}
          alt={item.name}
          className="er-img"
          onLoad={() => setLoaded(true)}
          style={{ opacity: loaded ? 1 : 0 }}
          loading="lazy"
          decoding="async"
        />
        {isEV && <span className="er-ev-badge">Electric</span>}
      </div>

      {/* Name + Price row */}
      <div className="er-info-row">
        <div className="er-name-block">
          <h3 className="er-car-name">{item.name}</h3>
          <p className="er-car-tagline">{item.desc}</p>
        </div>
        {specs.price && (
          <div className="er-price-block">
            <span className="er-starting">Starting at</span>
            <span className="er-price">{specs.price}</span>
          </div>
        )}
      </div>

      {/* Specs */}
      {(specs.mileage || specs.fuel) && (
        <div className="er-specs-row">
          {specs.mileage && (
            <span className="er-spec">
              {isEV ? <EVIcon /> : <MileageIcon />}
              {specs.mileage}
            </span>
          )}
          {specs.fuel && (
            <span className="er-spec">
              <FuelIcon />
              {specs.fuel}
            </span>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="er-btns">
        <button className="er-btn-primary" onClick={() => navigate(item.link)}>
          View Details
        </button>
        <button className="er-btn-secondary" onClick={() => navigate("/enquiry")}>
          Enquire →
        </button>
      </div>
    </div>
  );
}